/**
 * API Constants
 *
 * Centralized constants for the frontend application.
 * All timeouts, limits, and magic numbers should be defined here.
 */

export const API_CONSTANTS = {
  // Timeouts (milliseconds)
  COPY_FEEDBACK_DELAY: 2000,
  TOAST_ANIMATION_DELAY: 300,
  UI_FEEDBACK_DELAY: 500,
  ANALYSIS_POLL_INTERVAL: 3000,
  SYNC_DELAY: 2000,
  DEBOUNCE_DELAY: 1500,

  // Pagination
  PAGE_SIZE_DEFAULT: 12,
  PAGE_SIZE_LARGE: 100,
  SEARCH_LIMIT: 50,
  SEMANTIC_SEARCH_LIMIT: 20,

  // Input Limits
  CHAT_MAX_LENGTH: 500,
  PROMPT_FIELD_MAX_LENGTH: 500,

  // File Upload
  MAX_VIDEO_SIZE: 5 * 1024 * 1024 * 1024, // 5GB in bytes

  // Form Bounds - AI Settings
  TOKEN_LIMIT_MIN: 256,
  TOKEN_LIMIT_MAX: 65536,
  TOKEN_STEP: 256,
  TEMPERATURE_MIN: 0,
  TEMPERATURE_MAX: 2,
  TEMPERATURE_STEP: 0.1,
  WEBHOOK_TIMEOUT_MIN: 5,
  WEBHOOK_TIMEOUT_MAX: 300,
  WEBHOOK_RETRIES_MIN: 0,
  WEBHOOK_RETRIES_MAX: 10,

  // Pagination UI
  PAGINATION_MAX_VISIBLE: 7,
  PAGINATION_EDGE_COUNT: 5,

  // Cache TTL (milliseconds)
  SYNC_CACHE_TTL_MS: 30 * 60 * 1000, // 30 minutes
  DEFAULT_STALE_TIME: 5 * 60 * 1000, // 5 minutes - global default for TanStack Query
  RUBRICS_STALE_TIME: 5 * 60 * 1000, // 5 minutes
  SESSIONS_STALE_TIME: 30 * 1000, // 30 seconds
  RESULTS_STALE_TIME: 5 * 1000, // 5 seconds
} as const;

/**
 * Validation bounds for eval system
 */
export const EVAL_VALIDATION = {
  CRITERION_NAME_MAX: 100,
  CRITERION_DESCRIPTION_MIN: 10,
  CRITERION_DESCRIPTION_MAX: 500,
  CRITERION_WEIGHT_MIN: 0.1,
  CRITERION_WEIGHT_MAX: 10,
  SESSION_PROMPT_PACKS_MIN: 2,
  SESSION_PROMPT_PACKS_MAX: 30,
  BATCH_SIZE_MIN: 5,
  BATCH_SIZE_MAX: 10,
  K_REPEATS_MIN: 1,
  K_REPEATS_MAX: 5,
  RUBRIC_DESCRIPTION_MAX: 1000,
  RUBRIC_CRITERIA_MIN: 1,
  RUBRIC_CRITERIA_MAX: 20,
  JUDGE_TEMPERATURE_MIN: 0,
  JUDGE_TEMPERATURE_MAX: 1,
} as const;

/**
 * Route paths - centralized for navigation
 */
export const ROUTES = {
  HOME: '/',
  LOGIN: '/login',
  MEDIA_GALLERY: '/media-gallery',
  VIDEO_DETAIL: '/videos/:id',
  PROMPT_PACKS: '/prompt-packs',
  PROMPT_WIZARD: '/prompt-wizard',
  JUDGE_SETUP: '/judge-setup',
  PROMPT_EVAL: '/prompt-eval',
  SETTINGS: '/settings',
} as const;

/**
 * Available webhook event types
 */
export const WEBHOOK_EVENTS = [
  'video.analyzed',
  'video.uploaded',
  'analysis.completed',
  'analysis.failed',
  'decision.created',
  'decision.approved',
  'decision.rejected',
] as const;

export type WebhookEvent = typeof WEBHOOK_EVENTS[number];
